import { Box, Divider, Typography } from "@mui/material"
import { AddOutlined, CheckCircleFilledRounded } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"

import { BLANK_WAREHOUSE_MISCELLANEOUS_FEE } from "../../constants"
import type { WarehouseInfoFormValues } from "../../schemas"
import { formatTime, getWarehouseTypeLabel } from "../../utils"

import WarehouseLocationInfo from "./WarehouseLocationInfo"

import { Button } from "@/components/common/Button"
import { FormSelect, FormTextField } from "@/components/UiKit/FormControls"
import type { Company, Location } from "@/graphql/codegen/graphql"
import { WarehouseType } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface WarehouseInfoFormProps {
  company: Company
  location: Location
  externalSystemId: string
}

const WarehouseInfoForm: FC<WarehouseInfoFormProps> = ({ company, location, externalSystemId }) => {
  const { control, setValue, trigger } = useFormContext<WarehouseInfoFormValues>()
  const { fields, append } = useFieldArray({
    name: "miscellaneousFees",
  })

  const warehouseTypeOptions = useMemo((): Option<string>[] => {
    return Object.keys(WarehouseType).map((key) => ({
      value: WarehouseType[key as keyof typeof WarehouseType],
      label: getWarehouseTypeLabel(WarehouseType[key as keyof typeof WarehouseType]),
    }))
  }, [])

  const timeOptions = useMemo((): Option<string>[] => {
    const times: Option<string>[] = []
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00`
        times.push({ value, label: formatTime(value) })
      }
    }
    return times
  }, [])

  const handleFeeAdd = () => append(BLANK_WAREHOUSE_MISCELLANEOUS_FEE)

  const handleFeeChargeTypeChange = (value: string, index: number) => {
    setValue(`miscellaneousFees.${index}.type`, value, { shouldDirty: true, shouldValidate: true })
    trigger(`miscellaneousFees.${index}.amount`)
  }

  const handleFeeAmountChange = (value: string, index: number) => {
    setValue(`miscellaneousFees.${index}.amount`, value, { shouldDirty: true, shouldValidate: true })
    trigger(`miscellaneousFees.${index}.type`)
  }

  return (
    <Box data-testid='warehouse-info-form'>
      <Typography
        variant='body1'
        fontWeight={700}
        className='mb-4 flex items-center leading-6 tracking-[0.15px] text-gray-700'
      >
        1. Address Details
        <CheckCircleFilledRounded className='ml-2 size-[18px] text-success-500' />
      </Typography>
      <WarehouseLocationInfo
        className='mb-8'
        company={company}
        location={location}
        externalSystemId={externalSystemId}
      />
      <Typography variant='body1' fontWeight={700} className='mb-4 leading-6 tracking-[0.15px] text-gray-700'>
        2. Warehouse Details
      </Typography>
      <Box className='flex flex-col gap-6'>
        <FormSelect
          control={control}
          name='type'
          label='Warehouse Type'
          required
          options={warehouseTypeOptions}
          data-testid='warehouse-type-select'
        />
        <fieldset>
          <legend className='mb-6 font-medium tracking-[0.2px] text-gray-700'>Receiving Hours</legend>
          <Box className='flex gap-x-4'>
            <FormSelect
              control={control}
              name='receivingHoursFrom'
              label='From'
              options={timeOptions}
              data-testid='warehouse-receiving-hours-from-select'
              className='flex-1'
            />
            <FormSelect
              control={control}
              name='receivingHoursTo'
              label='To'
              options={timeOptions}
              data-testid='warehouse-receiving-hours-to-select'
              className='flex-1'
            />
          </Box>
        </fieldset>
        <fieldset>
          <legend className='mb-6 font-medium tracking-[0.2px] text-gray-700'>Shipping Hours</legend>
          <Box className='flex gap-x-4'>
            <FormSelect
              control={control}
              name='shippingHoursFrom'
              label='From'
              options={timeOptions}
              data-testid='warehouse-shipping-hours-from-select'
            />
            <FormSelect
              control={control}
              name='shippingHoursTo'
              label='To'
              options={timeOptions}
              data-testid='warehouse-shipping-hours-to-select'
            />
          </Box>
        </fieldset>
        <FormTextField
          control={control}
          name='specialInstructions'
          label='Special Instructions'
          multiline
          rows={4}
          data-testid='warehouse-special-instructions-input'
        />
        <FormTextField
          control={control}
          name='monthlyAccrualFee'
          label='Monthly Accrual'
          data-testid='warehouse-monthly-accrual-fee-input'
          slotProps={{
            input: {
              startAdornment: "$",
            },
          }}
        />
        <FormTextField
          control={control}
          name='inboundFee'
          label='Warehouse Inbound'
          data-testid='warehouse-inbound-fee-input'
          slotProps={{
            input: {
              startAdornment: "$",
            },
          }}
        />
        <FormTextField
          control={control}
          name='outboundFee'
          label='Warehouse Outbound'
          data-testid='warehouse-outbound-fee-input'
          slotProps={{
            input: {
              startAdornment: "$",
            },
          }}
        />
        <Divider className='my-6' />
        <Box className='flex flex-col'>
          <Box className='flex flex-col gap-6'>
            {fields.map((item, index) => (
              <Box key={item.id} className='flex w-full gap-x-4'>
                <FormTextField
                  control={control}
                  name={`miscellaneousFees.${index}.type`}
                  label='Charge Type'
                  className='flex-1'
                  data-test={`warehouse-misc-fee-type-input-${index}`}
                  onChange={(value) => handleFeeChargeTypeChange(value, index)}
                />
                <FormTextField
                  control={control}
                  name={`miscellaneousFees.${index}.amount`}
                  label='Charge Amount'
                  className='flex-1'
                  data-test={`warehouse-misc-fee-amount-input-${index}`}
                  onChange={(value) => handleFeeAmountChange(value, index)}
                  slotProps={{
                    input: {
                      startAdornment: "$",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
          <Button
            data-testid='warehouse-add-fee-button'
            variant='primary'
            appearance='text'
            className='mt-3 self-end'
            startIcon={<AddOutlined />}
            onClick={handleFeeAdd}
          >
            Miscellaneous Fees
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default WarehouseInfoForm
